import { computeDocument, type ComputedDocument } from '@core/calc';
import { asBasisPoints, asMoney, asQuantity, ZERO_QUANTITY } from '@core/money';
import { financialYearFor } from '@domain/financialYear';
import type { Business, Party } from '@data/db/types';
import type { ProductListItem } from '@data/services/productReader';
import type { InvoiceFormState, DraftLine } from '@features/invoices/invoiceFormState';

export interface QuotationFormState extends InvoiceFormState {
  readonly validityDate: string;
  readonly coverLetterEnabled: boolean;
  readonly coverLetter: string;
  readonly businessTerms: string;
}

export function blankQuotation(lineId: string, date: string): QuotationFormState {
  return {
    customerId: '', docDate: date, lines: [{ id: lineId, productId: '', quantity: '1', rate: '', description: '', unit: 'NOS', taxRateBp: 0, discountType: 'NONE', discountValue: 0 }],
    roundOff: false, placeOfSupplyOverride: '', validityDate: '', coverLetterEnabled: true,
    coverLetter: '', businessTerms: '',
  };
}

export function computeQuotationTotals(input: {
  business: Business;
  customer: Party | null;
  state: QuotationFormState;
  productsById: ReadonlyMap<string, ProductListItem>;
}): ComputedDocument | null {
  const { business, customer, state, productsById } = input;
  const lines = state.lines.filter((line) => line.productId && productsById.has(line.productId));
  if (!lines.length) return null;
  const place = state.placeOfSupplyOverride || customer?.shippingStateCode || customer?.stateCode || '';
  return computeDocument({
    business: { stateCode: business.stateCode, currency: 'INR' },
    docType: 'QUOTATION',
    docDate: state.docDate,
    financialYear: financialYearFor(state.docDate),
    placeOfSupplyCode: place,
    reverseCharge: false,
    lines: lines.map((line) => {
      const product = productsById.get(line.productId);
      const quantity = Number(line.quantity);
      const rupeeRate = Number(line.rate || 0);
      return {
        id: line.id,
        productId: line.productId,
        description: line.description.trim() || product?.product.name || 'Item',
        unit: product?.product.unit || 'NOS',
        quantity: asQuantity(Math.round((Number.isFinite(quantity) ? quantity : 0) * 1000)),
        freeQuantity: ZERO_QUANTITY,
        ratePaise: asMoney(Math.round((Number.isFinite(rupeeRate) ? rupeeRate : 0) * 100)),
        discountType: line.discountType,
        discountValue: line.discountValue,
        taxRateBp: asBasisPoints(line.taxRateBp ?? product?.taxRateBp ?? 0),
        cessRateBp: asBasisPoints(product?.product.cessRateBp ?? 0),
      };
    }),
    charges: [], invoiceDiscount: null, roundOff: state.roundOff,
    roundOffMode: business.documentSettings.roundOffMode, tcs: null,
  });
}

export function defaultQuotationCoverLetter(companyName: string): string {
  return [
    'Dear Sir/Madam,',
    '',
    'We are pleased to submit our quotation for your kind consideration.',
    'We hope you will find our offer competitive and look forward to your valued order.',
    '',
    'Thanking you,',
    'Yours faithfully,',
    `For ${companyName}`,
  ].join('\n');
}

export function draftLine(line: DraftLine, product?: ProductListItem) {
  const quantity = Number(line.quantity);
  const rate = Number(line.rate);
  return {
    productId: line.productId,
    quantityMilli: Math.round((Number.isFinite(quantity) ? quantity : 0) * 1000),
    ratePaise: Math.round((Number.isFinite(rate) ? rate : 0) * 100),
    ...(line.description.trim() ? { description: line.description.trim() } : {}),
    stockLocationId: undefined,
    discountType: line.discountType,
    discountValue: line.discountValue,
    taxRateBp: line.taxRateBp ?? product?.taxRateBp ?? 0,
  };
}
